import { NewMagicLinkForm } from './NewMagicLinkForm';
import { useProject } from '../hooks/useProject';
import toast from 'react-hot-toast';

interface ExpiredSessionFormProps {
  jiraKey: string;
  customerEmail?: string;
}

export function ExpiredSessionForm({ jiraKey, customerEmail }: ExpiredSessionFormProps) {
  const { project, loading } = useProject(jiraKey);

  // Show loading state while fetching project data
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <div className="flex flex-col items-center space-y-4">
              <img
                src="/ima_onboard_logo.png"
                alt="Ideal Mobile App Logo"
                className="h-20 w-20 object-contain"
              />
              <div className="flex flex-col items-center space-y-1">
                <h1 className="text-4xl font-bold text-gray-900">Ideal Mobile App</h1>
                <span className="text-xl text-gray-600 font-medium">Onboarding Portal</span>
              </div>
            </div>
          </div>
          <div className="text-center">
            <div className="animate-pulse">
              <div className="h-4 bg-gray-200 rounded w-3/4 mx-auto mb-2"></div>
              <div className="h-4 bg-gray-200 rounded w-1/2 mx-auto"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Get support contact info from project data, with fallback to both contacts
  const supportContact = {
    // Use project-specific contact if available, otherwise use defaults
    email: project?.supportContact?.email || 'support@idealss.com',
    phone: project?.supportContact?.phone || '+1 (555) 123-4567',
    hours: project?.supportContact?.hours || 'Mon-Fri 9AM-6PM EST',
    // Only include service contacts when no project exists at all
    ...(project ? {} : {
      versiRent: {
        email: 'fsdsupport@idealss.com',
        phone: '601-703-0324',
        hours: 'Mon-Fri 9AM-6PM EST (Extended hours available at additional cost)'
      },
      idealOne: {
        email: 'fecsupport@idealss.com',
        phone: '601-703-0165',
        hours: '24/7/364 (Just Christmas is a real Holiday)'
      }
    })
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        {/* Enhanced Branding Section */}
        <div className="text-center">
          <div className="flex flex-col items-center space-y-4">
            {/* Logo - Made larger and more prominent */}
            <img
              src="/ima_onboard_logo.png"
              alt="Ideal Mobile App Logo"
              className="h-20 w-20 object-contain"
            />
            {/* Company Name - Larger and bolder */}
            <div className="flex flex-col items-center space-y-1">
              <h1 className="text-4xl font-bold text-gray-900">Ideal Mobile App</h1>
              <span className="text-xl text-gray-600 font-medium">Onboarding Portal</span>
            </div>
          </div>
        </div>

        {/* Session Expired Message - Smaller and less prominent */}
        <div className="text-center">
          <h2 className="text-2xl font-semibold text-gray-800">
            Session Expired
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            Your session has expired. Please enter your email to get a new magic link.
          </p>
        </div>

        {/* Consolidated Magic Link Form */}
        <NewMagicLinkForm 
          jiraKey={jiraKey}
          customerEmail={customerEmail || ''}
          supportContact={supportContact}
          onMagicLinkGenerated={(email) => {
            toast.success('New magic link sent to your email! Please check your inbox.');
            console.log('Magic link sent to:', email);
          }}
        />
      </div>
    </div>
  );
}
